function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread(); }

function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray(arr); }

function _createForOfIteratorHelper(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (!it) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = it.call(o); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

/*
 * This source file contains the code for proxying calls in the master thread to calls in the workers
 * by `.postMessage()`-ing.
 *
 * Keep in mind that this code can make or break the program's performance! Need to optimize more…
 */
import DebugLogger from "debug";
import { multicast, Observable } from "observable-fns";
import { deserialize, serialize } from "../common";
import { ObservablePromise } from "../observable-promise";
import { isTransferDescriptor } from "../transferable";
import { MasterMessageType, WorkerMessageType } from "../types/messages";
var debugMessages = DebugLogger("threads:master:messages");
var nextJobUID = 1;

var dedupe = function dedupe(array) {
  return Array.from(new Set(array));
};

var isJobErrorMessage = function isJobErrorMessage(data) {
  return data && data.type === WorkerMessageType.error;
};

var isJobResultMessage = function isJobResultMessage(data) {
  return data && data.type === WorkerMessageType.result;
};

var isJobStartMessage = function isJobStartMessage(data) {
  return data && data.type === WorkerMessageType.running;
};

function createObservableForJob(worker, jobUID) {
  return new Observable(function (observer) {
    var asyncType;

    var messageHandler = function messageHandler(event) {
      debugMessages("Message from worker:", event.data);
      if (!event.data || event.data.uid !== jobUID) return;

      if (isJobStartMessage(event.data)) {
        asyncType = event.data.resultType;
      } else if (isJobResultMessage(event.data)) {
        if (asyncType === "promise") {
          if (typeof event.data.payload !== "undefined") {
            observer.next(deserialize(event.data.payload));
          }

          observer.complete();
          worker.removeEventListener("message", messageHandler);
        } else {
          if (event.data.payload) {
            observer.next(deserialize(event.data.payload));
          }

          if (event.data.complete) {
            observer.complete();
            worker.removeEventListener("message", messageHandler);
          }
        }
      } else if (isJobErrorMessage(event.data)) {
        var error = deserialize(event.data.error);

        if (asyncType === "promise" || !asyncType) {
          observer.error(error);
        } else {
          observer.error(error);
        }

        worker.removeEventListener("message", messageHandler);
      }
    };

    worker.addEventListener("message", messageHandler);
    return function () {
      if (asyncType === "observable" || !asyncType) {
        var cancelMessage = {
          type: MasterMessageType.cancel,
          uid: jobUID
        };
        worker.postMessage(cancelMessage);
      }

      worker.removeEventListener("message", messageHandler);
    };
  });
}

function prepareArguments(rawArgs) {
  if (rawArgs.length === 0) {
    // Exit early if possible
    return {
      args: [],
      transferables: []
    };
  }

  var args = [];
  var transferables = [];

  var _iterator = _createForOfIteratorHelper(rawArgs),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var arg = _step.value;

      if (isTransferDescriptor(arg)) {
        args.push(serialize(arg.send));
        transferables.push.apply(transferables, _toConsumableArray(arg.transferables));
      } else {
        args.push(serialize(arg));
      }
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  return {
    args: args,
    transferables: transferables.length === 0 ? transferables : dedupe(transferables)
  };
}

export function createProxyFunction(worker, method) {
  return function () {
    var uid = nextJobUID++;

    for (var _len = arguments.length, rawArgs = new Array(_len), _key = 0; _key < _len; _key++) {
      rawArgs[_key] = arguments[_key];
    }

    var _prepareArguments = prepareArguments(rawArgs),
        args = _prepareArguments.args,
        transferables = _prepareArguments.transferables;

    var runMessage = {
      type: MasterMessageType.run,
      uid: uid,
      method: method,
      args: args
    };
    debugMessages("Sending command to run function to worker:", runMessage);

    try {
      worker.postMessage(runMessage, transferables);
    } catch (error) {
      return ObservablePromise.from(Promise.reject(error));
    }

    return ObservablePromise.from(multicast(createObservableForJob(worker, uid)));
  };
}
export function createProxyModule(worker, methodNames) {
  var proxy = {};

  var _iterator2 = _createForOfIteratorHelper(methodNames),
      _step2;

  try {
    for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
      var methodName = _step2.value;
      proxy[methodName] = createProxyFunction(worker, methodName);
    }
  } catch (err) {
    _iterator2.e(err);
  } finally {
    _iterator2.f();
  }

  return proxy;
}